/** Creative Prompt editor with Asset Mention chips (Canvas Unified Video
 * Generation Node P0 §12–§17 + media UX unification V1 §24–§31).
 *
 * A `contentEditable` region whose persisted value carries
 * `assetMention.ts` markers. Chips render as thumbnail + alias + ×. */

import { useCallback, useEffect, useRef, useState, type DragEvent as ReactDragEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ASSET_MENTION_DND_TYPE, type AssetMentionDragPayload } from './InputMaterialsRow'
import { makeMentionToken, parsePromptText } from '../assetMention'

interface Props {
  value: string
  onChange: (next: string) => void
  /** Current live alias for every mentionable edge. */
  aliasByEdgeId: Map<string, string>
  /** Optional thumbnail URL keyed by edgeId (media UX §24–§26). */
  thumbUrlByEdgeId?: Map<string, string>
  placeholder?: string
}

function buildMentionChip(
  edgeId: string,
  aliasHint: string,
  currentAlias: string | undefined,
  thumbUrl: string | undefined,
  t: TFunction<'canvas'>,
): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = `pcv-mention-chip${currentAlias ? '' : ' pcv-mention-chip--disconnected'}`
  span.contentEditable = 'false'
  span.dataset.mentionEdgeId = edgeId
  span.dataset.mentionAliasHint = aliasHint

  if (thumbUrl) {
    const img = document.createElement('img')
    img.className = 'pcv-mention-chip-thumb'
    img.src = thumbUrl
    img.alt = ''
    img.draggable = false
    span.appendChild(img)
  } else {
    const ph = document.createElement('span')
    ph.className = 'pcv-mention-chip-thumb pcv-mention-chip-thumb--empty'
    ph.setAttribute('aria-hidden', 'true')
    span.appendChild(ph)
  }

  const label = document.createElement('span')
  label.className = 'pcv-mention-chip-label'
  label.textContent = currentAlias
    ? currentAlias
    : t('mention.disconnectedLabel', { alias: aliasHint || t('mention.materialFallback') })
  span.appendChild(label)

  const remove = document.createElement('button')
  remove.type = 'button'
  remove.className = 'pcv-mention-chip-remove nodrag'
  remove.setAttribute('aria-label', t('mention.removeReference'))
  remove.textContent = '×'
  remove.addEventListener('mousedown', (e) => {
    e.preventDefault()
    e.stopPropagation()
  })
  remove.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    const parent = span.parentNode
    span.remove()
    parent?.dispatchEvent(new Event('input', { bubbles: true }))
  })
  span.appendChild(remove)
  return span
}

function extractPromptText(root: Node): string {
  let out = ''
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent || ''
    } else if (child instanceof HTMLElement) {
      if (child.dataset.mentionEdgeId) {
        out += makeMentionToken(child.dataset.mentionEdgeId, child.dataset.mentionAliasHint || '')
      } else if (child.tagName === 'BR') {
        out += '\n'
      } else {
        out += extractPromptText(child)
        if (child.tagName === 'DIV' || child.tagName === 'P') out += '\n'
      }
    }
  }
  return out
}

export default function AssetMentionPromptEditor({ value, onChange, aliasByEdgeId, thumbUrlByEdgeId, placeholder }: Props) {
  const { t } = useTranslation('canvas')
  const elRef = useRef<HTMLDivElement | null>(null)
  const isFocusedRef = useRef(false)
  const [isFocused, setIsFocused] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const rebuildDom = useCallback(() => {
    const el = elRef.current
    if (!el) return
    el.innerHTML = ''
    const segments = parsePromptText(value)
    if (segments.length === 0) return
    for (const seg of segments) {
      if (seg.type === 'text') {
        if (seg.value) el.appendChild(document.createTextNode(seg.value))
      } else {
        el.appendChild(
          buildMentionChip(seg.edgeId, seg.aliasHint, aliasByEdgeId.get(seg.edgeId), thumbUrlByEdgeId?.get(seg.edgeId), t),
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, aliasByEdgeId, thumbUrlByEdgeId])

  useEffect(() => {
    if (isFocusedRef.current) return
    rebuildDom()
  }, [rebuildDom])

  const flush = useCallback(() => {
    const el = elRef.current
    if (!el) return
    onChange(extractPromptText(el))
  }, [onChange])

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false
    setIsFocused(false)
    flush()
  }, [flush])

  const handleInput = useCallback(() => {
    flush()
  }, [flush])

  const handleKeyDown = useCallback((e: ReactKeyboardEvent) => {
    e.stopPropagation()
  }, [])

  const handleDragOver = useCallback((e: ReactDragEvent) => {
    if (!e.dataTransfer.types.includes(ASSET_MENTION_DND_TYPE)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => setDragOver(false), [])

  const handleDrop = useCallback(
    (e: ReactDragEvent) => {
      setDragOver(false)
      const raw = e.dataTransfer.getData(ASSET_MENTION_DND_TYPE)
      if (!raw) return
      e.preventDefault()
      const el = elRef.current
      if (!el) return
      let payload: AssetMentionDragPayload
      try {
        payload = JSON.parse(raw)
      } catch {
        return
      }
      el.focus()
      const chip = buildMentionChip(
        payload.edgeId,
        payload.alias,
        aliasByEdgeId.get(payload.edgeId),
        payload.thumbUrl || thumbUrlByEdgeId?.get(payload.edgeId),
        t,
      )
      const trailingSpace = document.createTextNode(' ')
      const docWithCaret = document as Document & {
        caretRangeFromPoint?: (x: number, y: number) => Range | null
      }
      const range = docWithCaret.caretRangeFromPoint?.(e.clientX, e.clientY) ?? null
      if (range && el.contains(range.startContainer)) {
        range.insertNode(trailingSpace)
        range.insertNode(chip)
      } else {
        el.appendChild(chip)
        el.appendChild(trailingSpace)
      }
      const sel = window.getSelection()
      const after = document.createRange()
      after.setStartAfter(trailingSpace)
      after.collapse(true)
      sel?.removeAllRanges()
      sel?.addRange(after)
      flush()
    },
    [aliasByEdgeId, flush, thumbUrlByEdgeId, t],
  )

  const isEmpty = value.length === 0

  return (
    <div className="pcv-video-prompt-wrap">
      <div
        ref={elRef}
        className={`pcv-video-prompt-editor nodrag nowheel${expanded ? ' is-expanded' : ''}${dragOver ? ' is-drag-over' : ''}`}
        contentEditable
        suppressContentEditableWarning
        onFocus={handleFocus}
        onBlur={handleBlur}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-placeholder={placeholder}
      />
      {isEmpty && !isFocused ? <div className="pcv-video-prompt-placeholder">{placeholder}</div> : null}
      <button type="button" className="pcv-video-prompt-expand nodrag" onClick={() => setExpanded((v) => !v)}>
        {expanded ? t('generation.promptCollapse') : t('generation.promptExpand')}
      </button>
    </div>
  )
}
